import './ForgotPassword.css';
import '../common-style.css';

import { useContext, useState } from 'react';
import { requestPasswordReset } from '../../connector/auth';
import { sanitizeEmail } from 't-lib-ui/connector';
import { EmailLength } from '../../schema-copy/auth-schema';
import { useProgressModal } from 't-lib-ui/structure';
import { FlowForm, useFlowFormGlobal } from 't-lib-ui/structure';
import { Input } from 't-lib-ui/widgets';
import { TextButton } from 't-lib-ui/widgets';
import { Link, RouterContext, getLocation } from 't-lib-ui/router';
import { FormGrid, FormGridLine } from 't-lib-ui/structure';
import { createOnChangeHandler, validateEmail, validateInput } from 't-lib-ui/helpers';
import { ErrorNote } from 't-lib-ui/widgets';
import DownreadLogo from '../../components/DownreadLogo';

const emailErrorMessages = {
  missing: <>Please enter your email here.</>,
  bad: <>The email you have entered is not valid.<br/>Please check.</>
};

const statusErrorMessages = {
  connection: <>Failed to connect to server. Please try again later.</>,
  fields: <>Please check the email you have entered.</>
};

const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  const location = getLocation(routerCtx);
  useFlowFormGlobal();

  const [presentLoading, dismissLoading] = useProgressModal();

  const [email, setEmail] = useState(location.searchParams.get('email') ?? '');
  const [emailError, setEmailError] = useState<'missing'|'bad'|undefined>(undefined);

  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<'ok'|'loading'|'done'|'connection'|'fields'>('ok');
  const loginPath = `/login${email ? `?email=${encodeURIComponent(email)}` : ''}`;

  const validationNeededChecker = () => submitted;

  async function requestPasswordResetClicked() {
    setSubmitted(true);
    if (!validateInput(email, validateEmail, setEmailError)) {
      setStatus('fields');
      return true;
    }
    presentLoading({message: 'Requesting reset...'});
    setStatus('loading');

    try {
      await requestPasswordReset(email);
      setStatus('done');
    }
    catch (e) {
      setStatus('connection');
    }
    dismissLoading();
  }

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard forgotCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /></a>
          </div>
          <div className="authIntro">Reset your password.</div>
          <ErrorNote errorId={status} errorTexts={statusErrorMessages} />

          {status != 'done' ? <>
            <div className='forgotInfo'>If you cannot remember your password, enter your email address and we&apos;ll send you a reset link.</div>
            <FormGrid>
              <FormGridLine span={2} className='forgotInputRow'>
                <Input label='Email' type="email" layout='singleCol'
                  value={email}
                  errorId={emailError} errorTexts={emailErrorMessages}
                  enterKeyHint="enter"
                  maxLength={EmailLength}
                  onInput={createOnChangeHandler(setEmail, validateEmail, setEmailError, validationNeededChecker)} />
              </FormGridLine>
              <FormGridLine span={2} align='center' className='forgotSubmitRow'>
                <TextButton onClick={requestPasswordResetClicked} enabled={status == 'loading' ? 'disabled' : (status == 'fields' && !!emailError) ? 'error' : 'enabled'} type='major' label="Request" />
              </FormGridLine>
            </FormGrid>
            <div className="authLink forgotInlineLink">Found your password? <Link to={loginPath}>Log in</Link></div>
          </> : <>
            <div className='forgotInfo'>We sent an email to <strong>{sanitizeEmail(email)}</strong> with a password reset link. Please check your inbox and spam folder.</div>
            <div className='forgotInfo'>Got the email? <Link to={`/reset-password?email=${encodeURIComponent(email)}`}>Enter the code</Link> to set a new password.</div>
            <div className="authFooter forgotInlineLink">Ready to log in again? <Link to={loginPath}>Log in</Link></div>
          </>}
        </div>
      </div>
    </FlowForm>
  );
};

export default Page;
